import { useState } from "react";
import { LabelOwner, MskuPrepDetail, PrepOwner } from "@boxem/core-api-schema";

import PageContent from "@/components/PageContent";
import Button from "@/components/forms/Button/Button";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import IconUpload from "@/components/icons/IconUpload";
import IconInfoFilled from "@/components/icons/IconInfoFilled";
import Flex from "@/components/Flex/Flex";
import FbaProductsList from "./FbaProductsList/FbaProductsList";
import FbaShipmentItemsList from "./FbaShipmentItemsList/FbaShipmentItemsList";
import FbaUpdateShipmentDrawer from "../../FbaUpdateShipmentDrawer/FbaUpdateShipmentDrawer";

import styles from "./ChooseProductsPage.module.css";

import { FbaPageProps } from "../FbaShipmentFlow";
import PageHeader from "../PageHeader";
import IconPrintGoogle from "@/components/icons/IconPrintGoogle";
import { LabelsModal, LabelsModalProps } from "@/components/LabelsModal/LabelsModal";
import { getItemAssignedQuantity } from "@/contexts/ShipmentContext";

// given some prep details return the list of allowed prep owner values and label owner values
// for an item with these prep details. optionally, returns |valid| indicating passed values
// would be valid 
export function prepOwnerAndLabelOwnerValidForPrepDetails(
  prepDetails: MskuPrepDetail,
  prepOwner?: PrepOwner,
  labelOwner?: LabelOwner,
): { valid: boolean, allowedPrepOwners: PrepOwner[], suggestedPrepOwner: PrepOwner, allowedLabelOwners: LabelOwner[], suggestedLabelOwner: LabelOwner } {

  // from the docs: "Appropriate prepOwner values are returned in prepOwnerConstraint 
  // and appropriate labelOwner values are returned in labelOwnersConstraint. If these 
  // fields are empty in the response, you can select AMAZON or SELLER."
  // let prepOwners: PrepOwner[] = [PrepOwner.SELLER,PrepOwner.AMZON];
  // let labelOwners: LabelOwner[] = [LabelOwner.SELLER,LabelOwner.AMZON];

  // @HACK(nick) despite documentation saying these situations should not be possible, there are unfortunately
  // at least some cases of Amazon returning a label-owner constraint of 'NONE' (meaning 'NONE' should be the
  // only valid option) but the API then erroring saying 'Seller' or 'Amazon' is required. This ticket has this:
  // https://app.intercom.com/a/inbox/s7zia6wi/inbox/shared/mentions/conversation/3394?view=List
  // the reverse is also true occasionally, where the API says you should pass 'Seller' or 'Amazon', but in practice
  // it will actually only accept 'NONE'. This means the only way to be reliable is to allow the user all options in
  // all cases. We'll still try and be smart about autofilling/suggesting the 'correct' option:
  let prepOwners = [ PrepOwner.SELLER, PrepOwner.AMZON, PrepOwner.NONE ];
  let labelOwners = [ LabelOwner.SELLER, LabelOwner.AMZON, LabelOwner.NONE ];
  let suggestedPrepOwner = PrepOwner.SELLER;
  let suggestedLabelOwner = LabelOwner.SELLER;

  if (!prepDetails) {
    const valid = (!prepOwner || prepOwners.includes(prepOwner)) && (!labelOwner || labelOwners.includes(labelOwner));
    return { valid, allowedPrepOwners: prepOwners, suggestedPrepOwner, allowedLabelOwners: labelOwners, suggestedLabelOwner };
  }

  let prepOwnerAndLabelOwnerMustMatch: boolean = false;
  if (prepDetails.allOwnersConstraint) {
    prepOwnerAndLabelOwnerMustMatch = true;
  }

  switch (prepDetails.labelOwnerConstraint) {
    case "AMAZON_ONLY":
      suggestedLabelOwner = LabelOwner.AMZON;
      break;
    case "NONE_ONLY":
      suggestedLabelOwner = LabelOwner.NONE;
      break;
    case "SELLER_ONLY":
      suggestedLabelOwner = LabelOwner.SELLER;
      break;
  }

  if (prepOwnerAndLabelOwnerMustMatch) {
    // prepOwners = [...labelOwners] as any; // @HACK @FRAGILE
  } else {
    switch (prepDetails.prepOwnerConstraint) {
      case "AMAZON_ONLY":
        suggestedPrepOwner = PrepOwner.AMZON;
        break;
      case "NONE_ONLY":
        suggestedPrepOwner = PrepOwner.NONE;
        break;
      case "SELLER_ONLY":
        suggestedPrepOwner = PrepOwner.SELLER;
        break;
    }
  }

  const valid = (!prepOwner || prepOwners.includes(prepOwner)) && (!labelOwner || labelOwners.includes(labelOwner));
  return { valid, allowedPrepOwners: prepOwners, suggestedPrepOwner, allowedLabelOwners: labelOwners, suggestedLabelOwner };
}

export default function ChooseProductsPage({ batch, continueButton, onContinueClicked }: FbaPageProps) {
  const [updateFbaShipmentDrawerOpen, setUpdateFbaShipmentDrawerOpen] = useState(false);
  const [labelsModal, setLabelsModal] = useState<LabelsModalProps>();

  return (
    <PageContent
      header={
        <PageHeader
          step={0}
          shipmentType={batch.shipmentType}
          continueButton={continueButton}
          onContinueClicked={onContinueClicked}
        />
      }
    >
      <LabelsModal
        open={!!labelsModal}
        onClose={() => {
          setLabelsModal(null);
        }}
        items={labelsModal?.items ?? []}
      />

      <div className={styles.pageContent}>
        <FbaProductsList />

        <div className={styles.styledCol}>
          <Flex justify="end" gap={2}>
            <Button
              type="secondary"
              disabled={true}
            >
              <Tooltip title="Coming soon!">
                <Flex align="center" gap={2}>
                  <IconUpload />
                  <span>Bulk import</span>
                </Flex>
              </Tooltip>
            </Button>
            <Button
              type="secondary"
              onClick={() => setUpdateFbaShipmentDrawerOpen(true)}
            >
              <IconInfoFilled />
              <span>Shipment details</span>
            </Button>
             {/*  @NOTE(nick) future work will enable this:
             <Button
              type="secondary"
              onClick={() => {
                setLabelsModal({
                  open: true,
                  onClose: () => setLabelsModal(null),
                  items: batch.items.map(item => {
                    return {
                      image: item.image,
                      productCondition: item.condition,
                      name: item.name,
                      sku: item.sku,
                      fnsku: item.fnSku,
                      expirationDate: item.expirationDate,
                      totalQuantity: item.quantity,
                      assignedQuantity: getItemAssignedQuantity(batch, null, null, item.sku),
                    };
                  })
                });
              }}
            >
              <IconPrintGoogle/>
              <span>Print</span>
            </Button> */}
          </Flex>

          <FbaShipmentItemsList />
        </div>
        {updateFbaShipmentDrawerOpen && (
          <FbaUpdateShipmentDrawer
            open={updateFbaShipmentDrawerOpen}
            onClose={() => setUpdateFbaShipmentDrawerOpen(false)}
          />
        )}
      </div>
    </PageContent>
  );
}
